import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import Enllac from 'components/3catinfo/Enllac';
import Icona from 'components/3catinfo/ui/Icona';
import Image from 'next/image';
import { dataResources } from 'utils/constants';
import MenuLogin from '../MenuLogin';
import Dropdown from '../Dropdown';
import styles from './menusticky.module.scss';
import { useViewport } from 'hooks/3catinfo/useViewport';
import __isEmpty from 'lodash/isEmpty';
import { getItemsMenu } from '../funcions';
import { peuSideMenu } from '../../../corporatiu/MenuSecundari/contentMenu.jsx';

const { basePath } = dataResources;

const imageLoader = ({ src }) => {
    return `${src}`;
};

const ContentMenu = ({
    tipus,
    seccio,
    temesDestacats,
    informatius,
    temesGenerics,
    destacatsPortal,
    podcasts,
    isSideMenuOpen,
    setIsSideMenuOpen,
    accessibilityRefs
}) => {
    const items = getItemsMenu({ tipus, seccio, temesDestacats, informatius, podcasts, temesGenerics, destacatsPortal });
    const [dropdownOpened, setDropdownOpened] = useState(null);
    const { isMobile, zoomNavigator } = useViewport();
    const menuPrincipalRef = useRef();

    const handleDropdownToggle = (id) => {
        setDropdownOpened((prev) => (prev === id ? null : id));
    };

    const stylesContentMenu = clsx({
        [styles.contentMenu]: true,
        [styles.contentMenuDesktop]: true,
        [styles.isStickyM]: true,
        [styles.isClose]: isSideMenuOpen === 'closing',
        [styles.isOpen]: isSideMenuOpen === 'opened',
        [styles.isMobile]: isMobile,
        [styles.ambZoom]: zoomNavigator > 1.25,
        [styles.scrollMenu]: zoomNavigator >= 2 && !isMobile
    });

    const stylesBgContentMenu = clsx({
        [styles.bgContentMenu]: isSideMenuOpen === 'opened',
        [styles.isOpen]: isSideMenuOpen === 'opened'
    });

    const _buildElement = (item, i) => {
        const { id, type, text, slug, icona, icona_alt, opcions, href } = item;

        if (__isEmpty(opcions)) {
            const stylesLink = clsx({
                [styles.itemLink]: true,
                [styles.active]: slug !== undefined && slug === seccio
            });
            return (
                <div key={`menu-item-${i}`}>
                    <Enllac
                        dataTestId={`link-${slug}`}
                        className={stylesLink}
                        type={type}
                        id={id}
                        slug={slug}
                        href={href}
                        onClick={() => {
                            setIsSideMenuOpen('closing');
                            setTimeout(() => {
                                setIsSideMenuOpen('closed'); // Se ejecuta después de 500ms
                            }, 500);
                        }}
                        tabIndex={isSideMenuOpen === 'opened' ? '0' : '-1'}
                    >
                        {!__isEmpty(icona) && (
                            <Icona
                                className={styles.iconaMenu}
                                icona={icona}
                                alt={icona_alt}
                                mida='16'
                                inline={true}
                                ariaHidden={true}
                            />
                        )}
                        <span>{!__isEmpty(text) && text}</span>
                    </Enllac>
                </div>
            );
        } else {
            return (
                <div key={`menu-item-${i}`}>
                    <Dropdown
                        item={item}
                        dropdownOpened={dropdownOpened}
                        setDropdownOpened={handleDropdownToggle}
                        seccio={seccio}
                        isMobile={isMobile}
                        isMenuSticky={true}
                        setIsSideMenuOpen={setIsSideMenuOpen}
                        isSideMenuOpen={isSideMenuOpen}
                    />
                </div>
            );
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            handleCloseSideMenu();
        }
        if (e.key === 'Tab') {
            // Controla que el focus no surti del dropdown amb el Tab
            handleFocus(e);
        }
    };

    const handleCloseSideMenu = () => {
        if (isSideMenuOpen === 'opened') {
            const focusableElem = Array.from(menuPrincipalRef.current.querySelectorAll('button[aria-expanded="true"]'));
            if (focusableElem.length === 1) {
                setIsSideMenuOpen('closing');
                setTimeout(() => {
                    setIsSideMenuOpen('closed'); // Se ejecuta después de 500ms
                }, 500);
            }
        }
    };

    const handleFocus = (event) => {
        if (isSideMenuOpen) {
            const focusableElem = Array.from(
                menuPrincipalRef.current.querySelectorAll("a, button, [tabindex]:not([tabindex='-1'])")
            );

            const firstFocusableElem = focusableElem[0];
            const lastFocusableElem = focusableElem[focusableElem.length - 1];
            const isTabPressed = event.key === 'Tab';

            if (!isTabPressed) {
                return;
            }

            if (event.shiftKey) {
                if (document.activeElement === firstFocusableElem) {
                    lastFocusableElem.focus();
                    event.preventDefault();
                }
            } else if (document.activeElement === lastFocusableElem) {
                firstFocusableElem.focus();
                event.preventDefault();
            }
        }
    };

    useEffect(() => {
        if (isSideMenuOpen === 'opened') {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSideMenuOpen]);

    return (
        <nav
            aria-label='Menú principal'
            className={stylesBgContentMenu}
            id='mainMenu'
            data-opened='true'
            aria-hidden={isSideMenuOpen !== 'opened'}
        >
            <div className={stylesContentMenu} data-opened-menu='true' ref={menuPrincipalRef}>
                <div>
                    <button
                        ref={accessibilityRefs.closeMenu}
                        className={styles.ico_tanca}
                        onClick={(e) => {
                            setIsSideMenuOpen('closing');
                            setTimeout(() => {
                                setIsSideMenuOpen('closed'); // Se ejecuta después de 500ms
                            }, 500);
                        }}
                        aria-expanded={isSideMenuOpen === 'opened'}
                        aria-controls='mainMenu'
                        tabIndex={isSideMenuOpen === 'opened' ? '0' : '-1'}
                    >
                        <Icona icona='tanca' alt='Tanca' ariaHidden={true} />
                        <span className='sr-only'>Tanca menú de navegació</span>
                    </button>

                    <div className={styles.menuItems}>
                        <div className={styles.menuItemsItem}>
                            {!__isEmpty(items) &&
                                Array.from(items.item)
                                    .slice(0, 5)
                                    .map((element, i) => _buildElement(element, i))}
                        </div>
                        <div className={styles.menuItemsItem}>
                            {!__isEmpty(items) &&
                                Array.from(items.item)
                                    .slice(5)
                                    .map((element, i) => _buildElement(element, i))}
                        </div>
                    </div>
                </div>

                <>{peuSideMenu()}</>
            </div>
        </nav>
    );
};

const MenuDesktop = ({
    tipus = 'estatic',
    seccio,
    temesDestacats,
    informatius,
    temesGenerics,
    destacatsPortal,
    podcasts,
    estil,
    tema,
    titol,
    isSideMenuOpen,
    setIsSideMenuOpen,
    accessibilityRefs
}) => {
    const stylesMenu = clsx({
        [styles.menu]: true,
        [styles.menuDesktop]: true,
        [styles[estil]]: !__isEmpty(estil),
        [styles[tema]]: !__isEmpty(tema),
        [styles.mobilReduit]: !__isEmpty(titol)
    });

    return (
        <>
            <div className={stylesMenu} data-testid='menu'></div>

            <ContentMenu
                tipus={tipus}
                seccio={seccio}
                temesDestacats={temesDestacats}
                informatius={informatius}
                temesGenerics={temesGenerics}
                destacatsPortal={destacatsPortal}
                podcasts={podcasts}
                tema={tema}
                titol={titol}
                estil={estil}
                isSideMenuOpen={isSideMenuOpen}
                setIsSideMenuOpen={setIsSideMenuOpen}
                accessibilityRefs={accessibilityRefs}
            />
        </>
    );
};

const MenuCorporate = ({ requiredServices, isSideMenuOpen, setIsSideMenuOpen, seccio, accessibilityRefs, estil }) => {
    const { basePath } = dataResources;

    const stylesCorporate = clsx({
        [styles.corporate]: true,
        [styles.isSticky]: true,
        [styles.isNoti]: estil === 'itemNoticia'
    });

    return (
        <nav aria-label='Menú corporatiu' className={stylesCorporate} id='navegacio'>
            <div className={styles.barra_wrapper}>
                <div className={styles.barra}>
                    <div className={styles.barra_logo}>
                        <Enllac href='/3cat/'>
                            <div className={styles.logo3cat}>
                                <Image
                                    loader={imageLoader}
                                    unoptimized={true}
                                    width={82}
                                    height={24}
                                    alt='Logo 3Cat'
                                    src={basePath + '/_resources/logos/3cat_color.svg?v=JIT_ENV_RESOURCES_VERSION'}
                                />
                            </div>
                        </Enllac>
                    </div>
                    <div className={`${styles.iconesLink} ${styles.iconesUser} ${styles.logoRebaixat}`}>
                        <MenuLogin requiredServices={requiredServices} className={styles.login} />
                    </div>
                </div>
            </div>
            <div className={styles.wrapper}>
                <div className={styles.logo}>
                    <button
                        ref={accessibilityRefs.openMenu}
                        className={styles.ico_menu}
                        onClick={() => {
                            setIsSideMenuOpen('opened');
                        }}
                        aria-expanded={isSideMenuOpen === 'opened'}
                        aria-controls='mainMenu'
                    >
                        <Icona icona='menu' alt='Menu' isBlanc={false} mida={24} />
                    </button>
                    <Enllac href='/3catinfo/'>
                        {seccio === 'home' ? (
                            <h1 className={styles.logo3catinfo}>
                                <span className='sr-only'>3CatInfo, el portal informatiu de 3Cat</span>
                                <Image
                                    loader={imageLoader}
                                    loading='eager'
                                    unoptimized={true}
                                    width={119}
                                    height={24}
                                    alt='3CatInfo'
                                    src={basePath + '/_resources/logos/3catinfo_W.svg?v=JIT_ENV_RESOURCES_VERSION'}
                                />
                            </h1>
                        ) : (
                            <div className={styles.logo3catinfo}>
                                <Image
                                    loader={imageLoader}
                                    loading='eager'
                                    unoptimized={true}
                                    width={119}
                                    height={24}
                                    alt='3CatInfo'
                                    src={basePath + '/_resources/logos/3catinfo_W.svg?v=JIT_ENV_RESOURCES_VERSION'}
                                />
                            </div>
                        )}
                    </Enllac>
                </div>
                {/*<div className={styles.secundari}>
                    <div className={styles.icones}>
                        <div className={`${styles.iconesLink} ${styles.iconesUser} ${styles.logoRebaixat}`}>
                            <MenuLogin requiredServices={requiredServices} className={styles.login} />
                        </div>
                    </div>
                </div>*/}
            </div>
        </nav>
    );
};

const MenuSticky = ({
    requiredServices,
    isSideMenuOpen,
    setIsSideMenuOpen,
    seccio,
    tipus,
    estil,
    temesDestacats,
    informatius,
    temesGenerics,
    destacatsPortal,
    podcasts
}) => {
    //return <></>;
    const accessibilityRefs = {
        closeMenu: useRef(null),
        openMenu: useRef(null)
    };

    useEffect(() => {
        if (isSideMenuOpen === 'opened') {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollbarWidth}px`;
            accessibilityRefs.closeMenu.current?.focus();
        } else if (isSideMenuOpen === 'closing') {
            document.body.style.overflow = 'auto';
            document.body.style.paddingRight = '';
            accessibilityRefs.openMenu.current?.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSideMenuOpen]);

    return (
        <>
            <MenuCorporate
                requiredServices={requiredServices}
                isSideMenuOpen={isSideMenuOpen}
                setIsSideMenuOpen={setIsSideMenuOpen}
                accessibilityRefs={accessibilityRefs}
                estil={estil}
            />
            <MenuDesktop
                tipus={tipus}
                seccio={seccio}
                temesDestacats={temesDestacats}
                informatius={informatius}
                temesGenerics={temesGenerics}
                destacatsPortal={destacatsPortal}
                podcasts={podcasts}
                isSideMenuOpen={isSideMenuOpen}
                setIsSideMenuOpen={setIsSideMenuOpen}
                accessibilityRefs={accessibilityRefs}
            />
        </>
    );
};

export default MenuSticky;
